import { cn } from "@app/lib/utils";
import type { FootprintCountry } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { useEffect, useRef, useState } from "react";

/** Each country flashes its label in turn shortly after the globe appears. */
const REVEAL_DELAY = 700;
const REVEAL_STAGGER = 650;
const REVEAL_DURATION = 1400;

type CountryMarkerProps = {
  country: FootprintCountry;
  /** Position in the list — drives the staggered label reveal. */
  index: number;
  isActive: boolean;
  /** True once any country is selected, which dims the others. */
  hasSelection: boolean;
  prefersReducedMotion: boolean;
  onSelect: () => void;
  register: (key: string, element: SVGGElement | null) => void;
};

/**
 * A single country pin, living inside the globe's SVG so it shares the
 * projection's coordinate space. Its `transform`, `display` and pulse-ring
 * radii are written by `useGlobeProjection` on every frame — never here.
 */
export function CountryMarker({
  country,
  index,
  isActive,
  hasSelection,
  prefersReducedMotion,
  onSelect,
  register,
}: CountryMarkerProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const hasSelectionRef = useRef(hasSelection);

  hasSelectionRef.current = hasSelection;

  useEffect(() => {
    if (prefersReducedMotion) return;

    let hideTimer: ReturnType<typeof setTimeout>;

    const showTimer = setTimeout(
      () => {
        // Once the visitor has picked a country the tour is redundant.
        if (hasSelectionRef.current) return;

        setIsRevealed(true);
        hideTimer = setTimeout(() => setIsRevealed(false), REVEAL_DURATION);
      },
      REVEAL_DELAY + index * REVEAL_STAGGER,
    );

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [index, prefersReducedMotion]);

  const isLabelVisible = isActive || isRevealed || prefersReducedMotion;
  const isAccented = isActive || country.isHeadquarters;

  return (
    <g
      ref={element => register(country.key, element)}
      role="button"
      tabIndex={0}
      aria-label={trans(country.name)}
      aria-pressed={isActive}
      onClick={onSelect}
      onKeyDown={event => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        onSelect();
      }}
      className={cn(
        "group cursor-pointer outline-none transition-opacity duration-300",
        hasSelection && !isActive && "opacity-30",
      )}
    >
      {/* The glow the pin sits in. A gradient rather than a blur filter: the
          markers are re-placed on every frame of a rotation, and a filter would
          have the browser re-rasterise each one as it moved. Which gradient is
          only ever decided on a selection change, never mid-animation. */}
      <circle
        r={15}
        fill={isAccented ? "url(#fp-halo-hot)" : "url(#fp-halo-cool)"}
        className="pointer-events-none"
      />

      {/* Three rings, expanding out of phase. The animation loop owns their
          radius and opacity, so they start collapsed and invisible. */}
      {[0, 1, 2].map(ring => (
        <circle
          key={ring}
          data-pulse-ring
          r={6}
          fill="none"
          strokeWidth={1.4}
          opacity={0}
          className={cn(
            "pointer-events-none transition-colors duration-300",
            isActive ? "stroke-bright-orange" : "stroke-water-blue",
          )}
        />
      ))}

      {/* Invisible hit area — the visible dot shrinks with the SVG, and on a
          phone it is well under a comfortable tap target on its own. */}
      <circle r={16} fill="transparent" />

      {/* The pulse loop breathes this one's radius while it is selected, so its
          `r` is a starting value rather than a fixed size. */}
      <circle
        data-marker-core
        r={5.5}
        strokeWidth={1.5}
        className={cn(
          "stroke-white transition-[fill] duration-300",
          isAccented
            ? "fill-bright-orange"
            : "fill-water-blue group-hover:fill-bright-orange",
          "group-focus-visible:stroke-[3] group-focus-visible:stroke-aqua-blue",
        )}
      />

      <text
        dy={-16}
        textAnchor="middle"
        stroke="#04091f"
        strokeWidth={3}
        style={{ paintOrder: "stroke" }}
        className={cn(
          // Sized in viewBox units, so the type has to grow as the sphere
          // shrinks to stay legible on small screens.
          "pointer-events-none fill-[#eaf0fb] text-[22px] font-semibold uppercase tracking-[0.09em] transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100 sm:text-[17px] lg:text-[13px]",
          isLabelVisible ? "opacity-100" : "opacity-0",
        )}
      >
        {trans(country.name)}
      </text>
    </g>
  );
}
